import { z } from 'zod'

// A published, frozen snapshot of one production's script — what the trainer
// downloads and caches. Rails builds it (RunManifestBuilder) and the contract
// check in tools/contract validates the output against this schema.

export const LineKind = z.enum(['dialogue', 'direction', 'song', 'sound'])
export type LineKind = z.infer<typeof LineKind>

export const AudioSource = z.enum(['tts', 'recording', 'upload'])
export type AudioSource = z.infer<typeof AudioSource>

export const LineAudio = z.object({
  // Stable filename within the run; fetched from the authenticated audio endpoint.
  file: z.string(),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  durationMs: z.number().int().nonnegative(),
  source: AudioSource,
})
export type LineAudio = z.infer<typeof LineAudio>

export const Line = z.object({
  // Stable across script versions while the line exists, so progress survives revisions.
  id: z.string(),
  position: z.number().int().nonnegative(),
  kind: LineKind,
  // The primary speaker (voice, color). Null for stage directions and sound cues.
  characterId: z.string().nullable(),
  // Everyone who says the line, primary first — several for shared lines
  // ("MEG/MOTHER:"). Optional so manifests cached before it existed still load.
  characterIds: z.array(z.string()).optional(),
  text: z.string(),
  // Changes when the text changes; the trainer resets mastery for edited lines.
  textHash: z.string(),
  audio: LineAudio.nullable(),
  timing: z.object({
    // Silence before this line starts, after the previous line ends.
    gapBeforeMs: z.number().int().nonnegative(),
    // Starts this many ms before the previous line ends (interruptions, overlaps).
    overlapMs: z.number().int().nonnegative(),
  }),
})
export type Line = z.infer<typeof Line>

/** Everyone who says a line, primary first (falls back to characterId for older manifests). */
export function speakersOf(line: Pick<Line, 'characterId' | 'characterIds'>): string[] {
  return line.characterIds ?? (line.characterId ? [line.characterId] : [])
}

export const Scene = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  act: z.string().nullable(),
  title: z.string(),
  lines: z.array(Line),
})
export type Scene = z.infer<typeof Scene>

export const Character = z.object({
  id: z.string(),
  name: z.string(),
  // Display color so the trainer can tint each character consistently.
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
})
export type Character = z.infer<typeof Character>

export const RunManifest = z.object({
  schemaVersion: z.literal(1),
  production: z.object({ id: z.string(), title: z.string() }),
  version: z.number().int().positive(),
  publishedAt: z.iso.datetime(),
  characters: z.array(Character),
  scenes: z.array(Scene),
})
export type RunManifest = z.infer<typeof RunManifest>
